import { Component, Vue } from "vue-property-decorator";
import { CreateElement, VNode } from "vue";
import { AxiosError } from "axios";

import { RoomServiceAPI } from "@/api/room-service/room-service-api";
import { IRoomServiceRequest } from "@/@types/room-service/room-service";
import { isRoomServiceTypeHidden } from "@/services/featureConfig";

@Component({
  name: "room-service-checker",
  render: function (createElement: CreateElement): VNode {
    return createElement("div");
  },
})
export default class RoomServiceChecker extends Vue {
  pollInterval: ReturnType<typeof setInterval> | undefined;
  audio = require("@/assets/flute.mp3");

  startPoll(): void {
    this.fetchActive()
      .then((list) => this.handleList(list))
      .catch((e: AxiosError) => {
        console.log(e);
      });
  }

  async fetchActive(): Promise<IRoomServiceRequest[]> {
    const res = await RoomServiceAPI.getAll({ limit: 200 });
    if (res.status !== 200) return [];
    return res.data.data.filter((r) => !isRoomServiceTypeHidden(r.serviceType));
  }

  handleList(list: IRoomServiceRequest[]): void {
    const knownIds: string[] = this.$store.getters.getRoomServiceKnownIds;
    const initialized: boolean = this.$store.getters.getRoomServiceInitialized;
    const incomingIds = list.map((r) => r._id);

    const emergencyActive = list.filter(
      (r) =>
        r.serviceType === "EMERGENCY_ASSISTANCE" ||
        r.priority === "CRITICAL" ||
        r.status === "ESCALATED"
    );
    this.$store.dispatch(
      "setRoomServiceEmergencyCount",
      emergencyActive.length
    );

    if (!initialized) {
      this.$store.dispatch("setRoomServiceKnownIds", incomingIds);
      this.$store.dispatch("setRoomServiceNewCount", 0);
      this.$store.dispatch("setRoomServiceActiveRequests", list);
      this.$store.dispatch("setRoomServiceInitialized", true);
      return;
    }

    const newOnes = list.filter(
      (r) => !knownIds.includes(r._id) && r.status === "CREATED"
    );

    if (newOnes.length > 0) {
      this.notify(newOnes);
      this.$store.dispatch("setRoomServiceKnownIds", [
        ...knownIds,
        ...newOnes.map((r) => r._id),
      ]);
      this.$store.dispatch(
        "setRoomServiceNewCount",
        this.$store.getters.getRoomServiceNewCount + newOnes.length
      );
    }

    const missingIds = knownIds.filter((id) => incomingIds.includes(id));
    this.$store.dispatch("setRoomServiceKnownIds", [
      ...missingIds,
      ...newOnes.map((r) => r._id),
    ]);
    this.$store.dispatch("setRoomServiceActiveRequests", list);
  }

  notify(newOnes: IRoomServiceRequest[]): void {
    const emergency = newOnes.filter(
      (r) =>
        r.serviceType === "EMERGENCY_ASSISTANCE" ||
        r.priority === "CRITICAL" ||
        r.status === "ESCALATED"
    );
    const firstRoom = newOnes[0]?.room_number;
    const text =
      emergency.length > 0
        ? `<p>${String(
            this.$i18n.t("roomService.notify.emergency", {
              count: emergency.length,
              room: firstRoom,
            })
          )}</p>`
        : `<p>${String(
            this.$i18n.t("roomService.notify.new", {
              count: newOnes.length,
              room: firstRoom,
            })
          )}</p>`;

    this.$notify({
      group: "global",
      title: String(this.$i18n.t("roomService.notify.title")),
      text,
      type: emergency.length > 0 ? "error" : "success",
      duration: this.$second(300),
    });

    try {
      const audio = new Audio(this.audio);
      audio.play();
    } catch (e) {
      console.log(e);
    }
  }

  mounted(): void {
    if (!this.$getRight("getRoomServiceRequests")) return;
    this.startPoll();
    this.pollInterval = setInterval(() => {
      this.startPoll();
    }, this.$second(10));
  }

  destroyed(): void {
    clearInterval(this.pollInterval);
  }
}
